import { useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import { apiClient } from '@/shared/lib/api';
import { cacheKeys } from '@/shared/lib/cache-invalidation';

/**
 * Server-driven benefits/intro payload for the empty Album/Blueprint tab.
 *
 * All marketing copy (title, subtitle, bullets) is owned by the API
 * (`album.intro.*` lang files) — the client renders it verbatim and never
 * composes its own copy (API-driven messages rule). Mirrors the estimate intro
 * (`use-estimate-intro`); the album intro carries no region/units context.
 */
export interface AlbumIntroAttributes {
  title: string;
  subtitle: string;
  bullets: string[];
}

interface AlbumIntroResponse {
  data: {
    type: string;
    attributes: AlbumIntroAttributes;
  };
}

export function useAlbumIntro(conceptId: string) {
  const locale = useLocale();
  return useQuery({
    queryKey: cacheKeys.albums.intro(conceptId, locale),
    queryFn: async () => {
      const response = await apiClient.get<AlbumIntroResponse>(
        `/v1/concepts/${conceptId}/album/intro`
      );
      return response.data.attributes;
    },
    staleTime: 60 * 60 * 1000, // 1 hour — copy is static per locale
  });
}
